import { Injectable } from '@angular/core';
import * as signalR from '@microsoft/signalr';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { environment } from 'src/environments/environment';
import { jwtDecode } from 'jwt-decode';
import { Notificacao } from '../interfaces/notificacao.interface';
import { PendenciaAvisoEvento } from '../interfaces/pendencia/pendencia.interface';
import { ChamadoAvisoEvento } from '../interfaces/notificacao/notificacao-usuario.interface';
import { SessaoService } from './sessao.service';

@Injectable({
  providedIn: 'root'
})
export class WebSocketService {
  private hubConnection: signalR.HubConnection;

  private notificacoes = new BehaviorSubject<string | undefined>(null);
  private notificacoesModel = new BehaviorSubject<Notificacao | null>(null);

  // Subject, e não BehaviorSubject: quem assina depois não pode receber de novo um aviso antigo
  // (reabriria o toast de uma pendência já vista).
  private pendencias = new Subject<PendenciaAvisoEvento>();

  // Avisos de chamado (só para os participantes, Clients.Users). Subject pelo mesmo motivo.
  private chamados = new Subject<ChamadoAvisoEvento>();

  /**
   * Usuário (claim `sub`) com que a conexão atual se identificou no hub. Não o token bruto: a
   * renovação troca o token a cada poucos minutos, e o usuário continua o mesmo.
   */
  private usuarioConectado: string | null = null;

  constructor(private sessao: SessaoService) {
    this.startConnection();
  }

  private startConnection() {
    this.hubConnection = new signalR.HubConnectionBuilder()
      .withUrl(`${environment.APIEndpoint}pedidoHub`, {
        // hub exige [Authorize] — manda o JWT como access_token (lido em OnMessageReceived no backend).
        // Chamado a cada (re)conexão: renova antes se o token estiver vencendo, senão a reconexão
        // depois de uma queda (deploy, rede) falharia com token vencido.
        accessTokenFactory: async () => (await this.sessao.tokenParaEnvio()) ?? ''
      })
      .withAutomaticReconnect()
      .build();

    this.usuarioConectado = this.usuarioDoToken();
    this.hubConnection
      .start()
      .then(() => console.log('Conectado ao WebSocket!'))
      .catch(err => console.error('Erro ao conectar:', err));

    this.hubConnection.on('ReceiveNotification', (notificacao: Notificacao) => {
      console.log('Notificação DTO recebida:', notificacao);
      this.setNotificacoesModel(notificacao);
    });

    // Aviso de pendência: a API envia só para os participantes (Clients.Users), não para todos.
    this.hubConnection.on('ReceivePendencia', (aviso: PendenciaAvisoEvento) => {
      this.pendencias.next(aviso);
    });

    this.hubConnection.on('ReceiveChamado', (aviso: ChamadoAvisoEvento) => {
      this.chamados.next(aviso);
    });
  }

  /**
   * O hub identifica o usuário pelo token com que a conexão abriu. Depois de sair e entrar com
   * outro usuário sem recarregar a página, a conexão continuaria no nome do anterior — e os
   * avisos dirigidos (pendências) iriam para a pessoa errada. Reconecta quando o usuário mudou.
   */
  garantirConexaoDoTokenAtual(): void {
    const usuario = this.usuarioDoToken();
    if (!usuario || usuario === this.usuarioConectado) return;

    this.usuarioConectado = usuario;
    this.hubConnection
      .stop()
      .catch(() => { })
      .finally(() => {
        this.hubConnection.start().catch(err => console.error('Erro ao reconectar:', err));
      });
  }

  /**
   * Logout: fecha a conexão, que continuaria identificada como o usuário que saiu — e os avisos
   * dirigidos a ele apareceriam na tela de login para quem for entrar a seguir. O próximo login
   * reconecta por {@link garantirConexaoDoTokenAtual}.
   */
  desconectar(): void {
    this.usuarioConectado = null;
    this.hubConnection.stop().catch(() => { });
  }

  private usuarioDoToken(): string | null {
    const token = localStorage.getItem('token');
    if (!token) return null;
    try {
      return String((jwtDecode(token) as any)?.sub ?? '') || null;
    } catch {
      return null;
    }
  }

  getChamados(): Observable<ChamadoAvisoEvento> {
    return this.chamados.asObservable();
  }

  getPendencias(): Observable<PendenciaAvisoEvento> {
    return this.pendencias.asObservable();
  }

  getNotificacoes(){
    return this.notificacoes;
  }

  setNotificacao(){
    this.notificacoes.next("Novo Pedido Criado!");
  }

  getNotificacoesModel(){
    return this.notificacoesModel;
  }

  setNotificacoesModel(notificacao: Notificacao){
    this.notificacoesModel.next(notificacao);
  }

}
